// Wspólny komponent bazowy dla wszystkich kalkulatorów technicznych
import React, { ReactNode } from 'react';
import { 
  FileText, 
  Download, 
  Calculator,
  AlertTriangle,
  Info,
  Shield,
  CheckCircle,
  Gauge,
  Wind,
  Target,
  Cpu
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { convertPolishChars } from '../../utils/pdfFonts';

export interface CalculatorSection {
  title: string;
  icon: ReactNode;
  content: ReactNode;
  color?: string;
}

export interface CalculatorResult {
  parameters: Record<string, any>;
  results: Record<string, any>;
  warnings?: string[];
  tips?: string[];
  safetyNotes?: string[];
  qualityNotes?: string[];
}

interface TechnicalCalculatorLayoutProps {
  title: string;
  subtitle: string;
  icon: ReactNode;
  accentColor: string; // Kolor główny dla modułu (hex lub tailwind class)
  children: ReactNode;
  onGeneratePDF?: () => void;
}

export const TechnicalCalculatorLayout: React.FC<TechnicalCalculatorLayoutProps> = ({
  title,
  subtitle,
  icon,
  accentColor,
  children,
  onGeneratePDF
}) => {
  return (
    <div className="min-h-screen bg-zinc-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Nagłówek - zawsze taki sam styl */}
        <div className={`bg-gradient-to-r ${accentColor} rounded-xl p-8 mb-8`}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center">
              {icon}
            </div>
            <div>
              <h1 className="text-3xl font-bold">{title}</h1>
              <p className="text-white/80 mt-2">{subtitle}</p>
            </div>
          </div>
        </div>

        {/* Zawartość specyficzna dla kalkulatora */}
        {children}
      </div>
    </div>
  );
};

// Komponent dla sekcji wyników
export const ResultSection: React.FC<{
  title: string;
  icon: ReactNode;
  color: string;
  children: ReactNode;
}> = ({ title, icon, color, children }) => {
  return (
    <div className="bg-zinc-800 rounded-xl p-6">
      <h3 className={`font-bold mb-4 flex items-center gap-2 ${color}`}>
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
};

// Komponent dla kart z parametrami
export const ParameterCard: React.FC<{
  label: string;
  value: string | number;
  unit: string;
  color: number[]; // RGB
}> = ({ label, value, unit, color }) => {
  return (
    <div className="bg-zinc-700 rounded-lg p-4">
      <div className="text-sm text-zinc-400 mb-1">{label}</div>
      <div 
        className="text-2xl font-bold"
        style={{ color: `rgb(${color[0]}, ${color[1]}, ${color[2]})` }}
      >
        {value} {unit}
      </div>
    </div>
  );
};

// Komponent dla alertów
export const AlertBox: React.FC<{
  type: 'warning' | 'info' | 'success' | 'danger';
  title: string;
  children: ReactNode;
}> = ({ type, title, children }) => {
  const configs = {
    warning: {
      bg: 'bg-yellow-900/30',
      border: 'border-yellow-500/50',
      icon: <AlertTriangle className="w-5 h-5" />,
      textColor: 'text-yellow-400'
    },
    info: {
      bg: 'bg-blue-900/30',
      border: 'border-blue-500/50',
      icon: <Info className="w-5 h-5" />,
      textColor: 'text-blue-400'
    },
    success: {
      bg: 'bg-green-900/30',
      border: 'border-green-500/50',
      icon: <CheckCircle className="w-5 h-5" />,
      textColor: 'text-green-400'
    },
    danger: {
      bg: 'bg-red-900/30',
      border: 'border-red-500/50',
      icon: <AlertTriangle className="w-5 h-5" />,
      textColor: 'text-red-400'
    }
  };

  const config = configs[type];

  return (
    <div className={`${config.bg} border ${config.border} rounded-xl p-4`}>
      <h3 className={`font-bold mb-2 flex items-center gap-2 ${config.textColor}`}>
        {config.icon}
        {title}
      </h3>
      {children}
    </div>
  );
};

// Wspólna funkcja generowania PDF
export const generateTechnicalPDF = (
  moduleTitle: string,
  moduleType: string,
  parameters: Record<string, any>,
  results: Record<string, any>,
  additionalSections?: {
    warnings?: string[];
    tips?: string[];
    safetyNotes?: string[];
    qualityNotes?: string[];
    customSections?: Array<{
      title: string;
      items: string[];
      color?: number[]; // RGB
    }>;
  }
) => {
  const pdf = new jsPDF() as any;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Kolory standardowe
  const primaryColor = [249, 115, 22]; // orange-500
  const darkBg = [39, 39, 42];
  const dangerColor = [239, 68, 68];
  const warningColor = [245, 158, 11];
  const successColor = [34, 197, 94];
  const infoColor = [59, 130, 246];

  // Funkcja gradientu
  const drawGradient = (y: number, height: number, baseColor: number[]) => {
    const steps = 20;
    for (let i = 0; i < steps; i++) {
      const ratio = i / steps;
      const color = [
        baseColor[0] - ratio * 20,
        baseColor[1] - ratio * 10,
        baseColor[2] + ratio * 10
      ];
      pdf.setFillColor(color[0], color[1], color[2]);
      pdf.rect(0, y + (height / steps) * i, pageWidth, height / steps, 'F');
    }
  };

  // STRONA 1 - Nagłówek
  drawGradient(0, 45, primaryColor);

  // Logo
  pdf.setFillColor(255, 255, 255);
  pdf.circle(20, 22, 8, 'F');
  pdf.setTextColor(249, 115, 22);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text(moduleType.substring(0, 3).toUpperCase(), 20, 25, { align: 'center' });

  // Tytuł
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(24);
  pdf.text(convertPolishChars(moduleTitle), pageWidth / 2, 20, { align: 'center' });
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(convertPolishChars('Raport techniczny'), pageWidth / 2, 28, { align: 'center' });

  // Data i numer
  pdf.setFontSize(10);
  pdf.text(`Data: ${new Date().toLocaleDateString('pl-PL')}`, pageWidth - 20, 15, { align: 'right' });
  pdf.text(`#${Date.now().toString().slice(-8)}`, pageWidth - 20, 20, { align: 'right' });

  let y = 55;

  // Parametry wejściowe
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(39, 39, 42);
  pdf.text(convertPolishChars('PARAMETRY WEJSCIOWE'), 15, y);
  y += 8;

  const paramData = Object.entries(parameters).map(([key, value]) => [
    convertPolishChars(key),
    String(value)
  ]);

  if (paramData.length > 0) {
    pdf.autoTable({
      startY: y,
      head: [[convertPolishChars('Parametr'), convertPolishChars('Wartosc')]],
      body: paramData,
      theme: 'grid',
      headStyles: {
        fillColor: primaryColor,
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 10
      },
      margin: { left: 15, right: 15 }
    });
    y = pdf.lastAutoTable.finalY + 15;
  }

  // Wyniki
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text(convertPolishChars('WYNIKI OBLICZEN'), 15, y);
  y += 8;

  const resultData = Object.entries(results).map(([key, value]) => [
    convertPolishChars(key),
    String(value)
  ]);

  if (resultData.length > 0) {
    pdf.autoTable({
      startY: y,
      head: [[convertPolishChars('Parametr'), convertPolishChars('Wartosc')]],
      body: resultData,
      theme: 'striped',
      headStyles: {
        fillColor: darkBg,
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 10
      },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { fontStyle: 'bold' }
      },
      margin: { left: 15, right: 15 }
    });
    y = pdf.lastAutoTable.finalY + 15;
  }

  // STRONA 2 - jeśli są dodatkowe sekcje
  if (additionalSections && Object.values(additionalSections).some(v => v && v.length > 0)) {
    pdf.addPage();
    y = 20;

    // Nagłówek strony 2
    pdf.setFillColor(39, 39, 42);
    pdf.rect(0, 0, pageWidth, 30, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(convertPolishChars('Informacje Dodatkowe'), pageWidth / 2, 20, { align: 'center' });
    
    y = 40;

    // Ostrzeżenia
    if (additionalSections.warnings && additionalSections.warnings.length > 0) {
      pdf.setFillColor(254, 242, 242);
      pdf.roundedRect(15, y, pageWidth - 30, 10 + additionalSections.warnings.length * 6, 3, 3, 'F');
      
      pdf.setTextColor(239, 68, 68);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('[!] OSTRZEZENIA', 20, y + 7);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      let itemY = y + 14;
      additionalSections.warnings.forEach(warning => {
        pdf.text(`- ${convertPolishChars(warning)}`, 25, itemY);
        itemY += 6;
      });
      y = itemY + 5;
    }

    // Wskazówki
    if (additionalSections.tips && additionalSections.tips.length > 0) {
      pdf.setFillColor(239, 246, 255);
      pdf.roundedRect(15, y, pageWidth - 30, 10 + additionalSections.tips.length * 6, 3, 3, 'F');
      
      pdf.setTextColor(59, 130, 246);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('[i] WSKAZOWKI', 20, y + 7);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      let itemY = y + 14;
      additionalSections.tips.forEach(tip => {
        pdf.text(`- ${convertPolishChars(tip)}`, 25, itemY);
        itemY += 6;
      });
      y = itemY + 5;
    }

    // BHP
    if (additionalSections.safetyNotes && additionalSections.safetyNotes.length > 0) {
      pdf.setFillColor(255, 251, 235);
      pdf.roundedRect(15, y, pageWidth - 30, 10 + additionalSections.safetyNotes.length * 6, 3, 3, 'F');
      
      pdf.setTextColor(245, 158, 11);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('[BHP] ZASADY BHP', 20, y + 7);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      let itemY = y + 14;
      additionalSections.safetyNotes.forEach(note => {
        pdf.text(`- ${convertPolishChars(note)}`, 25, itemY);
        itemY += 6;
      });
    }
  }

  // Stopka na każdej stronie
  const addFooter = (pageNum: number, totalPages: number) => {
    pdf.setDrawColor(200, 200, 200);
    pdf.line(15, pageHeight - 20, pageWidth - 15, pageHeight - 20);
    
    pdf.setFontSize(9);
    pdf.setTextColor(128, 128, 128);
    pdf.setFont('helvetica', 'normal');
    
    pdf.text('PlexiSystem CRM', 15, pageHeight - 12);
    pdf.text('(c) 2025 PlexiSystem', 15, pageHeight - 8);
    
    const now = new Date();
    pdf.text(
      `${now.toLocaleDateString('pl-PL')} ${now.toLocaleTimeString('pl-PL')}`,
      pageWidth / 2,
      pageHeight - 12,
      { align: 'center' }
    );
    
    pdf.text(
      `Strona ${pageNum} z ${totalPages}`,
      pageWidth - 15,
      pageHeight - 12,
      { align: 'right' }
    );
    
    pdf.setFontSize(8);
    pdf.text('www.plexisystem.pl', pageWidth - 15, pageHeight - 8, { align: 'right' });
  };

  // Dodaj stopki
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    addFooter(i, pageCount);
  }

  // Zapisz
  const filename = `${moduleType}_${Date.now().toString().slice(-8)}.pdf`;
  pdf.save(filename);
};